import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FaPlus } from "react-icons/fa"
import { FiCopy } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { ConfigContainer, Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import ESignatureModal from "components/Lims/ESignatureModal"
import { limsSstTemplates, message } from "state_management"
import {
  SST_INSTRUMENT_TYPES,
  SST_TEMPLATE_STATES,
  instrumentTypeLabelKey,
  selectSstTemplates,
} from "state_management/lims/limsSstTemplates"
import { selectCanAdministerCapabilities } from "state_management/lims/limsCapabilities"

// Shared SST-template admin screen (F4, SPEC-036 / PRD-008). A capability-admin (planner /
// qa-manager) curates the team's reusable System-Suitability-Test criterion sets per INSTRUMENT
// TYPE (HPLC / GC). At a run the SST panel offers the LIVE templates for the analysis's instrument
// type and pre-fills the criteria from the pick; the analysis definition can name a default.
//
// Immutable versioning (GMP traceability): a template is never edited in place — "New version"
// supersedes the current live one and inserts a bumped copy (so a historical SST keeps the exact
// version it used). The screen lists every version, marking the live one and greying superseded
// rows. Editing is plain stamdata maintenance — audit-tracked, NOT e-signed.
//
// Gated on selectCanAdministerCapabilities (mirrors the server: POST/version are 403 for non-admins).
// The whole route is also feature-flagged behind showLims in App.jsx.
export default function SstTemplates() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const templates = useSelector(selectSstTemplates)
  const canAdminister = useSelector(selectCanAdministerCapabilities)

  // null = closed; { mode: "create" } = new template; { mode: "version", base } = new version of base.
  const [editing, setEditing] = useState(null)

  useEffect(() => {
    dispatch(limsSstTemplates.fetch())
  }, [dispatch])

  // Non-admins should never reach here (route is admin-gated), but guard the UI too so a deep link
  // shows a clear message rather than a broken editing surface.
  if (!canAdminister) {
    return (
      <div className="LimsSstTemplates">
        <div className="LimsSstTemplates-header">
          <h1 className="LimsSstTemplates-title">{t("limsSstTemplatesTitle")}</h1>
        </div>
        <p className="LimsSstTemplates-empty">{t("limsSstTemplatesAdminOnly")}</p>
      </div>
    )
  }

  const handleSave = (record) => {
    const isVersion = editing && editing.mode === "version"
    const payload = {
      name: (record.name || "").trim(),
      instrumentType: record.instrumentType,
      rsdAreaMax: numOrNull(record.rsdAreaMax),
      rsdRtMax: numOrNull(record.rsdRtMax),
      tailingMin: numOrNull(record.tailingMin),
      tailingMax: numOrNull(record.tailingMax),
      plateCountMin: numOrNull(record.plateCountMin),
      resolutionMin: numOrNull(record.resolutionMin),
      injectionCount: numOrNull(record.injectionCount),
    }
    const verb = isVersion
      ? dispatch(limsSstTemplates.createVersion(editing.base.id, payload))
      : dispatch(limsSstTemplates.create(payload))
    verb
      .then(() => {
        dispatch(message.success(t(isVersion ? "limsSstTemplateVersioned" : "limsSstTemplateCreated")))
        // Re-fetch so the just-superseded row's supersededAt is reflected (versioning path).
        if (isVersion) dispatch(limsSstTemplates.fetch())
        setEditing(null)
      })
      .catch(() => dispatch(message.error(t("limsSstTemplateSaveFailed"))))
  }

  return (
    <div className="LimsSstTemplates">
      <div className="LimsSstTemplates-header">
        <h1 className="LimsSstTemplates-title">{t("limsSstTemplatesTitle")}</h1>
        <Button variant="primary" onClick={() => setEditing({ mode: "create" })}>
          <FaPlus /> {t("limsSstTemplateAdd")}
        </Button>
      </div>
      <p className="LimsSstTemplates-intro">{t("limsSstTemplatesIntro")}</p>

      {SST_INSTRUMENT_TYPES.map((type) => {
        const forType = templates
          .filter((tpl) => tpl.instrumentType === type)
          .sort(
            (a, b) =>
              (a.name || "").localeCompare(b.name || "") || (a.versionNumber || 0) - (b.versionNumber || 0)
          )
        return (
          <div key={type} className="LimsSstTemplates-group">
            <h2 className="LimsSstTemplates-group-title">{t(instrumentTypeLabelKey(type))}</h2>
            {forType.length === 0 ? (
              <p className="LimsSstTemplates-empty">{t("limsSstTemplatesNoneInType")}</p>
            ) : (
              <Table striped hover responsive className="LimsSstTemplates-table">
                <thead>
                  <tr>
                    <th>{t("limsSstTemplateName")}</th>
                    <th>{t("limsSstTemplateVersion")}</th>
                    <th>{t("limsSstRsdAreaMax")}</th>
                    <th>{t("limsSstRsdRtMax")}</th>
                    <th>{t("limsSstTailing")}</th>
                    <th>{t("limsSstPlateCountMin")}</th>
                    <th>{t("limsSstResolutionMin")}</th>
                    <th>{t("limsSstInjectionCount")}</th>
                    <th>{t("limsSstTemplateStatus")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {forType.map((tpl) => {
                    const isEffective = tpl.state === SST_TEMPLATE_STATES.EFFECTIVE
                    const isSuperseded = tpl.state === SST_TEMPLATE_STATES.SUPERSEDED
                    return (
                      <tr key={tpl.id} className={isSuperseded ? "LimsSstTemplates-row--superseded" : undefined}>
                        <td>{tpl.name}</td>
                        <td>v{tpl.versionNumber}</td>
                        <td>{fmtBound(tpl.rsdAreaMax)}</td>
                        <td>{fmtBound(tpl.rsdRtMax)}</td>
                        <td>{fmtRange(tpl.tailingMin, tpl.tailingMax)}</td>
                        <td>{fmtBound(tpl.plateCountMin)}</td>
                        <td>{fmtBound(tpl.resolutionMin)}</td>
                        <td>{fmtBound(tpl.injectionCount)}</td>
                        <td>
                          <span className={"LimsSstTemplates-status " + statusClass(tpl.state)}>
                            {t(statusLabelKey(tpl.state))}
                          </span>
                        </td>
                        <td className="LimsSstTemplates-actions">
                          {/* SPEC-039 / F7: the reviewer/supervisor e-sign actions on a draft /
                              reviewer-approved version; "new version" on the effective one. */}
                          <ApprovalActions tpl={tpl} />
                          {isEffective && (
                            <FiCopy
                              className="LimsSstTemplates-version-icon"
                              title={t("limsSstTemplateNewVersion")}
                              onClick={() => setEditing({ mode: "version", base: tpl })}
                            />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
          </div>
        )
      })}

      {editing && (
        <SstTemplateConfig
          mode={editing.mode}
          base={editing.base || null}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

// Add / new-version form for a single template, rendered in a Popup with a ConfigContainer (which
// owns the save/cancel buttons via submitHandler/cancelHandler, mirroring ConsumableConfig). A new
// template defaults to the first instrument type; a new version is pre-filled from the base version
// (its name + instrument type are fixed — versioning stays within one lineage). Name + instrument
// type are required; submitHandler returns false to keep the dialog open when invalid (fail-fast).
function SstTemplateConfig({ mode, base, onClose, onSave }) {
  const { t } = useT("common")
  const isVersion = mode === "version"
  const [record, setRecord] = useState(() =>
    base
      ? { ...base }
      : {
          name: "",
          instrumentType: SST_INSTRUMENT_TYPES[0],
          rsdAreaMax: "",
          rsdRtMax: "",
          tailingMin: "",
          tailingMax: "",
          plateCountMin: "",
          resolutionMin: "",
          injectionCount: 6,
        }
  )
  const setField = (field, value) => setRecord((r) => ({ ...r, [field]: value }))

  const handleSubmit = () => {
    if (!(record.name || "").trim() || !record.instrumentType) return false
    onSave(record)
    return true
  }

  return (
    <Popup nonScrollable onCancel={onClose}>
      <ConfigContainer
        title={isVersion ? t("limsSstTemplateNewVersion") : t("limsSstTemplateAdd")}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
      >
        <Container>
          <Title>
            {t("limsSstTemplateName")} <div className="red-text">*</div>
          </Title>
          {/* The name + instrument type are fixed on a version bump (the lineage key). */}
          <Text value={record.name || ""} onChange={(v) => setField("name", v)} disabled={isVersion} />
        </Container>
        <Container>
          <Title>{t("limsSstInstrumentType")}</Title>
          <Select
            isMulti={false}
            collection={SST_INSTRUMENT_TYPES.map((type) => ({ id: type, name: t(instrumentTypeLabelKey(type)) }))}
            value={record.instrumentType}
            onChange={(v) => setField("instrumentType", v[0] || SST_INSTRUMENT_TYPES[0])}
            menuPlacement="bottom"
            disabled={isVersion}
          />
        </Container>
        <CriterionInput label={t("limsSstRsdAreaMax")} field="rsdAreaMax" record={record} set={setField} />
        <CriterionInput label={t("limsSstRsdRtMax")} field="rsdRtMax" record={record} set={setField} />
        <CriterionInput label={t("limsSstTailingMin")} field="tailingMin" record={record} set={setField} />
        <CriterionInput label={t("limsSstTailingMax")} field="tailingMax" record={record} set={setField} />
        <CriterionInput label={t("limsSstPlateCountMin")} field="plateCountMin" record={record} set={setField} />
        <CriterionInput label={t("limsSstResolutionMin")} field="resolutionMin" record={record} set={setField} />
        <CriterionInput label={t("limsSstInjectionCount")} field="injectionCount" record={record} set={setField} />
      </ConfigContainer>
    </Popup>
  )
}

function CriterionInput({ label, field, record, set }) {
  const value = record[field]
  return (
    <Container>
      <Title>{label}</Title>
      <Text
        type="number"
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(v) => set(field, v)}
      />
    </Container>
  )
}

// A raw entered scalar → finite number or null (blank / garbage → null). The server is the source
// of truth; this just keeps unset criteria as null (not judged).
function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null
  const n = Number(typeof v === "string" ? v.replace(",", ".") : v)
  return Number.isFinite(n) ? n : null
}

function fmtBound(v) {
  return v === null || v === undefined || v === "" ? "—" : String(v)
}
function fmtRange(min, max) {
  const hasMin = min !== null && min !== undefined && min !== ""
  const hasMax = max !== null && max !== undefined && max !== ""
  if (hasMin && hasMax) return `${min} – ${max}`
  if (hasMin) return `≥ ${min}`
  if (hasMax) return `≤ ${max}`
  return "—"
}

// SPEC-039 / F7 — the reviewer→supervisor e-sign approval actions for ONE template version. A
// `draft` offers Reviewer-approve (capability "reviewer", 4-eyes ≠ the version creator); a
// `reviewer-approved` version offers Supervisor-approve (capability "supervisor", 4-eyes ≠ the
// reviewer signer). On supervisor approval the version becomes effective and the prior effective
// version is superseded server-side. Reuses ESignatureModal + canSign exactly as the analysis-
// document register does. Effective / superseded versions show no approval action here.
function ApprovalActions({ tpl }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [signing, setSigning] = useState(null) // "reviewer" | "supervisor" | null
  const [saving, setSaving] = useState(false)

  const label = `${tpl.name} v${tpl.versionNumber}`

  const handleReviewerApprove = (signature) => {
    setSaving(true)
    return dispatch(limsSstTemplates.reviewerApprove(tpl.id, signature))
      .then(() => {
        setSigning(null)
        dispatch(message.success(t("limsSstTemplateReviewerApproved", { name: label })))
      })
      .finally(() => setSaving(false))
  }

  const handleSupervisorApprove = (signature) => {
    setSaving(true)
    return dispatch(limsSstTemplates.supervisorApprove(tpl.id, signature))
      .then(() => {
        setSigning(null)
        // Re-fetch so the just-superseded prior effective version reflects its new state.
        dispatch(limsSstTemplates.fetch())
        dispatch(message.success(t("limsSstTemplateSupervisorApproved", { name: label })))
      })
      .finally(() => setSaving(false))
  }

  // 4-eyes exclusions: the reviewer cannot be the creator; the supervisor cannot be the reviewer.
  const creatorId = tpl.createdBy
  const reviewerId = tpl.reviewerSignature && tpl.reviewerSignature.memberId

  return (
    <>
      {tpl.state === SST_TEMPLATE_STATES.DRAFT && (
        <Button variant="primary" size="sm" disabled={saving} onClick={() => setSigning("reviewer")}>
          {t("limsForskriftReviewerApprove")}
        </Button>
      )}
      {tpl.state === SST_TEMPLATE_STATES.REVIEWER_APPROVED && (
        <Button variant="success" size="sm" disabled={saving} onClick={() => setSigning("supervisor")}>
          {t("limsForskriftSupervisorApprove")}
        </Button>
      )}

      {signing === "reviewer" && (
        <ESignatureModal
          title={t("limsSstTemplateReviewerApproveTitle")}
          requiredCapability="reviewer"
          excludedMemberIds={creatorId ? [creatorId] : []}
          onSign={handleReviewerApprove}
          onCancel={() => setSigning(null)}
        />
      )}
      {signing === "supervisor" && (
        <ESignatureModal
          title={t("limsSstTemplateSupervisorApproveTitle")}
          requiredCapability="supervisor"
          excludedMemberIds={reviewerId ? [reviewerId] : []}
          onSign={handleSupervisorApprove}
          onCancel={() => setSigning(null)}
        />
      )}
    </>
  )
}

function statusLabelKey(state) {
  if (state === SST_TEMPLATE_STATES.EFFECTIVE) return "limsSstTemplateLive"
  if (state === SST_TEMPLATE_STATES.SUPERSEDED) return "limsSstTemplateSuperseded"
  if (state === SST_TEMPLATE_STATES.REVIEWER_APPROVED) return "limsForskriftStatusReviewerApproved"
  return "limsForskriftStatusDraft"
}

function statusClass(state) {
  if (state === SST_TEMPLATE_STATES.EFFECTIVE) return "is-live"
  if (state === SST_TEMPLATE_STATES.SUPERSEDED) return "is-superseded"
  if (state === SST_TEMPLATE_STATES.REVIEWER_APPROVED) return "is-reviewer"
  return "is-draft"
}
